import {
  Table,
  Button,
  Checkbox,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  message,
  Space,
  Upload,
  Popconfirm,
  Tooltip,
  Tabs,
  Divider,
} from 'antd';
import {
  DownloadOutlined,
  SendOutlined,
  CommentOutlined,
  PlusCircleOutlined,
  SyncOutlined,
  FolderOutlined,
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  CloseOutlined,
  MenuOutlined,
  FileOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { saveAs } from 'file-saver';
import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAuth } from '@/redux/auth/selectors';
import TextArea from 'antd/lib/input/TextArea';
import { Switch } from 'antd';
import AutoCompleteAsync from '@/components/AutoCompleteAsync';
import ActionMultipleAutoCompleteAsync from '@/forms/WorkstreamActionAutoCompleteAsync';
import { determinePlanningSummary } from '@/utils/planningSummary';
import { makeApiRequest } from '@/components/Kanban/request/apiRequest';
import { getObfuscatedPath } from '@/utils/apiRoute';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { notification } from 'antd';

const { TabPane } = Tabs;

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            zIndex: 1,
            width: '10px',
            height: '100%',
            cursor: 'col-resize',
            background: '#1890ff',
            opacity: 0,
            transition: 'opacity 0.3s',
            borderRadius: '2px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '0';
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const MemoizedCommentItem = memo(({ commentObj }) => (
  <div
    style={{
      marginBottom: '8px',
      padding: '12px',
      border: '1px solid #e8e8e8',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      transition: 'all 0.3s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f0f5ff';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '#fafafa';
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
      <CommentOutlined
        style={{
          backgroundColor: '#1890ff',
          color: 'white',
          padding: '4px',
          borderRadius: '50%',
          marginRight: '8px',
          fontSize: '12px',
        }}
      />
      <strong style={{ fontSize: '14px' }}>
        {commentObj.commentedBy?.firstName} {commentObj.commentedBy?.lastName}
      </strong>
      <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
        {moment(commentObj.commentedDate).format('YYYY-MM-DD HH:mm')}
      </span>
    </div>
    <div style={{ paddingLeft: '28px', fontSize: '14px', color: '#333' }}>
      {commentObj.comment}
    </div>
  </div>
));

const MemoizedActivityItem = memo(({ activity, getIcon, showChangeDetails }) => (
  <div
    style={{
      marginBottom: '8px',
      padding: '12px',
      border: '1px solid #e8e8e8',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      transition: 'all 0.3s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f0f5ff';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '#fafafa';
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
      <span style={{ marginRight: '8px' }}>{getIcon(activity.event)}</span>
      <strong style={{ fontSize: '14px' }}>
        {activity.owner?.firstName} {activity.owner?.lastName}
      </strong>
      <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
        {moment(activity.eventDate).format('YYYY-MM-DD HH:mm')}
      </span>
    </div>
    <div style={{ paddingLeft: '28px', fontSize: '14px', color: '#333' }}>
      {activity.event}
      {activity.changes && Object.keys(activity.changes).length > 0 && (
        <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
          {Object.entries(activity.changes).map(([key, change], changeIndex) => {
            if (key === 'comments') return null;
            return (
              <Typography.Link
                key={changeIndex}
                onClick={() => showChangeDetails(change)}
                style={{ marginRight: '8px' }}
              >
                {key}
                {changeIndex < Object.entries(activity.changes).length - 1 ? ', ' : ''}
              </Typography.Link>
            );
          })}
        </div>
      )}
    </div>
  </div>
));

const MemoizedAttachmentItem = memo(({ attachment, handleDownloadAttachment, allowToEdit, handleDeleteAttachment }) => (
  <div
    style={{
      marginBottom: '8px',
      padding: '12px',
      border: '1px solid #e8e8e8',
      borderRadius: '8px',
      backgroundColor: '#fafafa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      transition: 'all 0.3s ease',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = '#f0f5ff';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = '#fafafa';
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <FileOutlined style={{ fontSize: '20px', color: '#1890ff', marginRight: '12px' }} />
      <div>
        <div style={{ fontSize: '14px', color: '#333', fontWeight: 500 }}>
          {attachment.name}
        </div>
        <div style={{ fontSize: '12px', color: '#888' }}>
          Type: {attachment.type || 'General'} • Uploaded: {attachment.uploadedDate ? moment(attachment.uploadedDate).format('YYYY-MM-DD HH:mm') : 'N/A'}
        </div>
      </div>
    </div>
    <Space>
      <Tooltip title="Download">
        <DownloadOutlined
          onClick={() => handleDownloadAttachment(attachment.path)}
          style={{ cursor: 'pointer', color: '#1890ff', fontSize: '16px' }}
        />
      </Tooltip>
      {allowToEdit && (
        <Popconfirm
          title="Are you sure you want to delete this attachment?"
          onConfirm={() => handleDeleteAttachment(attachment.path)}
          okText="Yes"
          cancelText="No"
        >
          <Tooltip title="Delete">
            <DeleteOutlined style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: '16px' }} />
          </Tooltip>
        </Popconfirm>
      )}
    </Space>
  </div>
));

const { Option } = Select;

// Allowed sources for Executive Assistant role
const EXECUTIVE_ASSISTANT_ALLOWED_SOURCES = ['CMC', 'EMC', 'AMC', 'Office of CEO'];
const RESTRICTED_SOURCES = ['AMC', 'CMC', 'EMC', 'Office of CEO'];
const WorkstreamactionTable = ({
  actionItem: initialActionItem,
  otherElementRef,
  colorByAttribute,
  actionItemColors,
  visibleColumn,
  users,
  setActionItemCurrentFilter,
  setFilterVisible,
  actionItemCurrentFilter,
  setFilterHistory
}) => {
  const obfucatePath = getObfuscatedPath('user');
  const currentUser = useSelector(selectAuth);
  const history = useHistory();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form] = Form.useForm();
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [selectedTab, setSelectedTab] = useState('attachments');
  const [changeDetails, setChangeDetails] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [saveing, setSaving] = useState(false);
  const [actionItem, setActionItem] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [dynamicPadding, setDynamicPadding] = useState(20);
  const commentsActivitiesRef = useRef(null);
  const [attachmentForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  // State for column widths
  const [columnWidths, setColumnWidths] = useState({});

  // const hasSpecialAccess = currentUser?.hasSpecialAccess === true;
  const isCTO = currentUser?.role?.name === 'CTO';
  const isExecutiveAssistant = currentUser?.role?.name === 'Executive Assistant';
  const isAdmin = currentUser?.role?.name === 'Admin';

  const isStrategyAnalyst = currentUser?.role?.name === 'Strategy Analyst';
  const isTOFinance = currentUser?.role?.name === 'TO Finance';

  // Check if user can edit a specific item
  const canEditItem = useCallback((item) => {
    if (isCTO) return true;
    if (isExecutiveAssistant) {
      return EXECUTIVE_ASSISTANT_ALLOWED_SOURCES.includes(item?.source);
    }
    return false;
  }, [isCTO, isExecutiveAssistant]);

  // Check if user can edit the currently selected record
  const allowToEdit = useMemo(() => {
    if (!selectedRecord) return false;
    return canEditItem(selectedRecord);
  }, [selectedRecord, canEditItem]);

  // Helper function to get user name from either ID string or user object
  const getUserName = useCallback((userValue) => {
    if (!userValue) return null;
    if (typeof userValue === 'object' && userValue.firstName) {
      return `${userValue.firstName} ${userValue.lastName || ''}`.trim();
    }
    if (typeof userValue === 'string' && users) {
      const user = users.find((u) => u._id === userValue);
      if (user) {
        return `${user.firstName} ${user.lastName}`.trim();
      }
    }
    return null;
  }, [users]);

  // Helper function to format change value
  const formatChangeValue = useCallback((value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const userName = getUserName(value);
      if (userName) return userName;
      return value;
    }
    if (typeof value === 'object') {
      if (value.firstName || value.lastName) {
        return `${value.firstName || ''} ${value.lastName || ''}`.trim();
      }
      if (Array.isArray(value)) {
        return value
          .map((item) => {
            if (typeof item === 'string') {
              const userName = getUserName(item);
              return userName || item;
            }
            if (item && typeof item === 'object') {
              if (item.firstName || item.lastName) {
                return `${item.firstName || ''} ${item.lastName || ''}`.trim();
              }
              return JSON.stringify(item);
            }
            return item;
          })
          .join(', ');
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, [getUserName]);

  // Handle column resize
  const handleResize = useCallback((key) => (e, { size }) => {
    setColumnWidths(prev => ({
      ...prev,
      [key]: size.width
    }));
  }, []);

  // Memoize the filtered action items based on user permissions
  const myWorkstreamActionData = useMemo(() => {
    if (!initialActionItem || !currentUser) return [];

    return initialActionItem.filter((action) => {
      // Helper to check if user is the owner
      const isOwner = (() => {
        if (action?.owner) {
          if (typeof action.owner === 'object' && action.owner._id) {
            return action.owner._id.toString() === currentUser.id.toString();
          } else if (typeof action.owner === 'string') {
            return action.owner.toString() === currentUser.id.toString();
          } else if (typeof action.owner === 'object' && action.owner.id) {
            return action.owner.id.toString() === currentUser.id.toString();
          }
        }
        return false;
      })();

      // Helper to check if user is a collaborator
      const isCollaborator = action?.collaborator?.some((colla) => {
        if (!colla) return false;
        let collaId = null;
        if (typeof colla === 'object' && colla._id) {
          collaId = colla._id;
        } else if (typeof colla === 'object' && colla.id) {
          collaId = colla.id;
        } else if (typeof colla === 'string') {
          collaId = colla;
        }
        return collaId && collaId.toString() === currentUser.id.toString();
      });

      // Check if user has full access (Admin only)
      const hasFullAccess = isAdmin;

      // If user has full access (Admin only)
      if (hasFullAccess) {
        // Admin can see everything (read-only)
        return true;
      }

      // If user is Executive Assistant
      if (isExecutiveAssistant) {
        // Check if source is allowed
        const hasAllowedSource = EXECUTIVE_ASSISTANT_ALLOWED_SOURCES.includes(action?.source);

        // Show if user is owner, collaborator, OR has allowed source
        return isOwner || isCollaborator || hasAllowedSource;
      }

      // If user is CTO, Strategy Analyst, or TO Finance
      // They can see all non-restricted sources
      if (isCTO || isStrategyAnalyst || isTOFinance) {
        // Cannot see restricted sources
        if (RESTRICTED_SOURCES.includes(action?.source)) {
          return false;
        }
        // Can see all non-restricted sources
        return true;
      }

      // For other users, use the existing logic
      const ownerWorkstream =
        action?.owner?.workstream?._id === currentUser?.workstream?._id ||
        action?.owner?.subworkstream?.workstream?._id === currentUser?.subworkstream?.workstream?._id;
      const ownerSubworkstream =
        action?.subworkstream?._id === currentUser?.subworkstream?._id;

      const collaboratorWorkstreams = action?.collaborator?.some(
        (colla) =>
          colla?.workstream?._id === currentUser?.workstream?._id ||
          colla?.subworkstream?.workstream?._id === currentUser?.subworkstream?.workstream?._id
      );

      const collaboratorSubworkstreams = action?.collaborator?.some(
        (colla) =>
          colla?.subworkstream?._id === currentUser?.subworkstream?._id
      );

      return (
        ownerWorkstream ||
        ownerSubworkstream ||
        collaboratorWorkstreams ||
        collaboratorSubworkstreams
      );
    });
  }, [initialActionItem, currentUser, isExecutiveAssistant, isAdmin, isCTO, isStrategyAnalyst, isTOFinance]);
  // Initialize state when myWorkstreamActionData changes
  useEffect(() => {
    setActionItem(myWorkstreamActionData);
    setFilteredData(myWorkstreamActionData);
  }, [myWorkstreamActionData]);

  // Memoize columns definition with resize support
  const allColumns = useMemo(() => [
    {
      title: 'Workstream Action Number',
      dataIndex: 'actionItemNumber',
      key: 'actionItemNumber',
      width: columnWidths['actionItemNumber'] || 180,
      sorter: (a, b) => parseInt(a.actionItemNumber) - parseInt(b.actionItemNumber),
      filterMultiple: true,
      filters: [
        ...new Set(
          actionItem.map((item) => ({ text: item.actionItemNumber, value: item.actionItemNumber }))
        ),
      ],
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('actionItemNumber');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (actionItemNumber, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {colorByAttribute === 'actionItemNumber' &&
            record.actionItemNumber &&
            actionItemColors[record.actionItemNumber] && (
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: actionItemColors[record.actionItemNumber],
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              />
            )}
          {actionItemNumber}
        </div>
      ),
    },
    {
      title: 'Workstream Action Description',
      dataIndex: 'workstreamActionName',
      key: 'workstreamActionName',
      width: columnWidths['workstreamActionName'] || 600,
      minWidth: 200,
      sorter: (a, b) => (a.workstreamActionName || '').localeCompare(b.workstreamActionName || ''),
      filterMultiple: true,
      filters: [
        ...new Set(
          actionItem.map((item) => ({
            text: item.workstreamActionName,
            value: item.workstreamActionName,
          }))
        ),
      ],
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('workstreamActionName');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (workstreamActionName, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'row',
            width: '100%',
          }}
        >
          {colorByAttribute === 'workstreamActionName' &&
            record.workstreamActionName &&
            actionItemColors[record.workstreamActionName] && (
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: actionItemColors[record.workstreamActionName],
                  marginRight: '8px',
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              />
            )}
          <div
            style={{
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              width: '100%',
              flex: 1,
              lineHeight: '1.5em',
            }}
            title={workstreamActionName}
          >
            {workstreamActionName || ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Subworkstream',
      dataIndex: ['owner', 'subworkstream', 'subworkstreamName'],
      key: 'subworkstream',
      width: columnWidths['subworkstream'] || 180,
      sorter: (a, b) =>
        (a.owner?.subworkstream?.subworkstreamName || '').localeCompare(
          b.owner?.subworkstream?.subworkstreamName || ''
        ),
      filters: Array.from(
        new Map(
          actionItem.map((item) => {
            const value = item.owner?.subworkstream?.subworkstreamName || 'No Subworkstream';
            return [value, { text: value, value }];
          })
        ).values()
      ),
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('Accountable subworkstream');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (subworkstream, record) => {
        const swsName = subworkstream || 'No Subworkstream';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'Accountable Subworkstream' &&
              record.owner?.subworkstream?.subworkstreamName &&
              actionItemColors[swsName] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[swsName],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {swsName}
          </div>
        );
      },
    },
    {
      title: 'Workstream',
      dataIndex: ['owner', 'subworkstream', 'workstream', 'workstreamName'],
      key: 'workstream',
      width: columnWidths['workstream'] || 180,
      sorter: (a, b) => {
        const aWorkstreamName =
          a.owner?.role?.name === 'Workstream Sponsor'
            ? a.owner?.workstream?.workstreamName
            : a.owner?.subworkstream?.workstream?.workstreamName || '';
        const bWorkstreamName =
          b.owner?.role?.name === 'Workstream Sponsor'
            ? b.owner?.workstream?.workstreamName
            : b.owner?.subworkstream?.workstream?.workstreamName || '';
        return aWorkstreamName.localeCompare(bWorkstreamName);
      },
      filters: Array.from(
        new Map(
          actionItem.map((item) => {
            const value =
              item.owner?.role?.name === 'Workstream Sponsor'
                ? item.owner?.workstream?.workstreamName || 'No Workstream'
                : item.owner?.subworkstream?.workstream?.workstreamName || 'No Workstream';
            return [value, { text: value, value }];
          })
        ).values()
      ),
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('Accountable Workstream');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (text, record) => {
        const workstreamName =
          record.owner?.role?.name === 'Workstream Sponsor'
            ? record.owner?.workstream?.workstreamName || 'No Workstream'
            : record.owner?.subworkstream?.workstream?.workstreamName || 'No Workstream';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'Accountable Workstream' &&
              workstreamName &&
              actionItemColors[workstreamName] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[workstreamName],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {workstreamName}
          </div>
        );
      },
    },
    {
      title: 'Owner',
      dataIndex: 'owner',
      key: 'owner',
      width: columnWidths['owner'] || 180,
      sorter: (a, b) => {
        const ownerA = a.owner;
        const ownerB = b.owner;
        const nameA = typeof ownerA === 'object' ? `${ownerA?.firstName || ''} ${ownerA?.lastName || ''}`.trim() : formatChangeValue(ownerA);
        const nameB = typeof ownerB === 'object' ? `${ownerB?.firstName || ''} ${ownerB?.lastName || ''}`.trim() : formatChangeValue(ownerB);
        return nameA.localeCompare(nameB);
      },
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('owner');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (owner, record) => {
        let ownerName = 'Not Assigned';
        if (owner) {
          if (typeof owner === 'object' && (owner.firstName || owner.lastName)) {
            ownerName = `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
          } else if (typeof owner === 'string') {
            const userName = getUserName(owner);
            ownerName = userName || owner;
          }
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'owner' && record.owner && actionItemColors[ownerName] && (
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: actionItemColors[ownerName],
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              />
            )}
            {ownerName}
          </div>
        );
      },
    },
    {
      title: 'Collaborator',
      dataIndex: 'collaborator',
      key: 'collaborator',
      width: columnWidths['collaborator'] || 200,
      sorter: (a, b) => {
        const collabA = (a.collaborator || [])
          .map((col) => {
            if (typeof col === 'object' && (col.firstName || col.lastName)) {
              return `${col.firstName || ''} ${col.lastName || ''}`.trim();
            }
            if (typeof col === 'string') {
              const userName = getUserName(col);
              return userName || col;
            }
            return '';
          })
          .join(', ');
        const collabB = (b.collaborator || [])
          .map((col) => {
            if (typeof col === 'object' && (col.firstName || col.lastName)) {
              return `${col.firstName || ''} ${col.lastName || ''}`.trim();
            }
            if (typeof col === 'string') {
              const userName = getUserName(col);
              return userName || col;
            }
            return '';
          })
          .join(', ');
        return collabA.localeCompare(collabB);
      },
      filters: Array.from(
        new Map(
          actionItem.flatMap(
            (item) =>
              item.collaborator?.map((col) => {
                let name = '';
                if (typeof col === 'object' && (col.firstName || col.lastName)) {
                  name = `${col.firstName || ''} ${col.lastName || ''}`.trim();
                } else if (typeof col === 'string') {
                  const userName = getUserName(col);
                  name = userName || col;
                }
                return [name, { text: name, value: name }];
              }) || []
          )
        ).values()
      ),
      onFilter: (value, record) =>
        record.collaborator?.some((col) => {
          let name = '';
          if (typeof col === 'object' && (col.firstName || col.lastName)) {
            name = `${col.firstName || ''} ${col.lastName || ''}`.trim();
          } else if (typeof col === 'string') {
            const userName = getUserName(col);
            name = userName || col;
          }
          return name === value;
        }),
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (collaborator, record) => {
        const collabNames = collaborator?.length
          ? collaborator.map((col) => {
            if (typeof col === 'object' && (col.firstName || col.lastName)) {
              return `${col.firstName || ''} ${col.lastName || ''}`.trim();
            }
            if (typeof col === 'string') {
              const userName = getUserName(col);
              return userName || col;
            }
            return '';
          }).join(', ')
          : 'No Collaborator';
        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
              {colorByAttribute === 'Collaborator' &&
                record.collaborator &&
                actionItemColors[collabNames] && (
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: actionItemColors[collabNames],
                      marginRight: '8px',
                      flexShrink: 0,
                    }}
                  />
                )}
            </div>
            <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{collabNames}</div>
          </div>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: columnWidths['status'] || 150,
      sorter: (a, b) => (a.status || '').localeCompare(b.status || ''),
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('status');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (status, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {colorByAttribute === 'status' && record.status && actionItemColors[record.status] && (
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: actionItemColors[record.status],
                marginRight: '8px',
                flexShrink: 0,
              }}
            />
          )}
          {status}
        </div>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: columnWidths['source'] || 150,
      sorter: (a, b) => (a.source || '').localeCompare(b.source || ''),
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('source');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (source, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {colorByAttribute === 'source' && record?.source && actionItemColors[record?.source] && (
            <span
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '2px',
                backgroundColor: actionItemColors[record?.source],
                marginRight: '8px',
                flexShrink: 0,
              }}
            />
          )}
          {source}
        </div>
      ),
    },
    {
      title: 'Planning Summary',
      key: 'planningSummary',
      width: columnWidths['planningSummary'] || 180,
      sorter: (a, b) =>
        (determinePlanningSummary(a) || '').localeCompare(determinePlanningSummary(b) || ''),
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('planningSummary');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (_, record) => {
        const summary = determinePlanningSummary(record);
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'Action planning summary' && summary && actionItemColors[summary] && (
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: actionItemColors[summary],
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              />
            )}
            {summary}
          </div>
        );
      },
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      width: columnWidths['comment'] || 600,
      sorter: (a, b) => (a.comment || '').localeCompare(b.comment || ''),
      filterMultiple: true,
      filters: [
        ...new Set(actionItem.map((item) => ({ text: item.comment, value: item.comment }))),
      ],
      onFilter: (value, record) => record.comment === value,
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (comment) => (
        <div
          style={{
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            width: '100%',
            lineHeight: '1.5em',
          }}
          title={comment}
        >
          {comment || ''}
        </div>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: columnWidths['dueDate'] || 120,
      sorter: (a, b) => new Date(a.dueDate) - new Date(b.dueDate),
      filters: [
        ...new Set(
          actionItem.map((item) => ({
            text: item.dueDate ? moment(item.dueDate).format('YYYY-MM-DD') : 'No Due Date',
            value: item.dueDate,
          }))
        ),
      ],
      onFilter: (value, record) => record.dueDate === value,
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (_, record) => {
        const createdOnDate = record.dueDate ? moment(record.dueDate).format('YYYY-MM-DD') : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'dueDate' && record.dueDate && actionItemColors[createdOnDate] && (
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '2px',
                  backgroundColor: actionItemColors[createdOnDate],
                  marginRight: '8px',
                  flexShrink: 0,
                }}
              />
            )}
            {createdOnDate}
          </div>
        );
      },
    },
    {
      title: 'Created on',
      key: 'Created on',
      width: columnWidths['Created on'] || 120,
      sorter: (a, b) => new Date(a.created || 0) - new Date(b.created || 0),
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (_, record) => {
        const createdOnDate = record.created ? moment(record.created).format('YYYY-MM-DD') : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'Created on' &&
              record.created &&
              actionItemColors[createdOnDate] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[createdOnDate],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {createdOnDate}
          </div>
        );
      },
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('Created on');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
    },
    {
      title: 'Updated on',
      key: 'Updated on',
      width: columnWidths['Updated on'] || 120,
      sorter: (a, b) => {
        const updatedA =
          a.activity?.find((event) =>
            event.event?.includes(`Workstream action ${a.actionItemNumber} is updated`)
          )?.eventDate || 0;
        const updatedB =
          b.activity?.find((event) =>
            event.event?.includes(`Workstream action ${b.actionItemNumber} is updated`)
          )?.eventDate || 0;
        return new Date(updatedA) - new Date(updatedB);
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (_, record) => {
        const updatedEvent = record.activity?.find((event) =>
          event.event?.includes(`Workstream action ${record.actionItemNumber} is updated`)
        );
        const updatedOnDate = updatedEvent?.eventDate
          ? moment(updatedEvent.eventDate).format('YYYY-MM-DD')
          : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'Updated on' &&
              updatedEvent?.eventDate &&
              actionItemColors[updatedOnDate] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[updatedOnDate],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {updatedOnDate}
          </div>
        );
      },
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('Updated on');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
    },
    {
      title: 'Updated by',
      key: 'updatedBy',
      width: columnWidths['updatedBy'] || 180,
      sorter: (a, b) => {
        const updatedOwnerA = a.activity?.find((event) =>
          event.event?.includes(`Workstream action ${a.actionItemNumber} is updated`)
        )?.owner;
        const updatedOwnerB = b.activity?.find((event) =>
          event.event?.includes(`Workstream action ${b.actionItemNumber} is updated`)
        )?.owner;
        const nameA = updatedOwnerA
          ? `${updatedOwnerA.firstName || ''} ${updatedOwnerA.lastName || ''}`.trim().toLowerCase()
          : '';
        const nameB = updatedOwnerB
          ? `${updatedOwnerB.firstName || ''} ${updatedOwnerB.lastName || ''}`.trim().toLowerCase()
          : '';
        return nameA.localeCompare(nameB);
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (_, record) => {
        const updatedEvent = record.activity?.find((event) =>
          event.event?.includes(`Workstream action ${record.actionItemNumber} is updated`)
        );
        const updatedOwner = updatedEvent?.owner;
        const updatedOwnerName = updatedOwner ? `${updatedOwner.firstName || ''} ${updatedOwner.lastName || ''}`.trim()
          : 'N/A';
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'UpdatedBy' &&
              updatedOwnerName !== 'N/A' &&
              actionItemColors[updatedOwnerName] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[updatedOwnerName],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {updatedOwnerName}
          </div>
        );
      },
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('Updated By');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
    },
    {
      title: 'Created by',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: columnWidths['createdBy'] || 180,
      sorter: (a, b) => {
        const createdByA = a.createdBy;
        const createdByB = b.createdBy;
        const nameA = typeof createdByA === 'object'
          ? `${createdByA?.firstName || ''} ${createdByA?.lastName || ''}`.trim()
          : formatChangeValue(createdByA);
        const nameB = typeof createdByB === 'object'
          ? `${createdByB?.firstName || ''} ${createdByB?.lastName || ''}`.trim()
          : formatChangeValue(createdByB);
        return nameA.localeCompare(nameB);
      },
      filterDropdown: () => null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          setFilterVisible(true);
          setActionItemCurrentFilter('createdBy');
          setFilterHistory((prevHistory) => [
            ...prevHistory,
            actionItemCurrentFilter,
            'Filter Options',
          ]);
        }
      },
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(column.key),
      }),
      render: (createdBy, record) => {
        let createdByName = 'N/A';
        if (createdBy) {
          if (typeof createdBy === 'object' && (createdBy.firstName || createdBy.lastName)) {
            createdByName = `${createdBy.firstName || ''} ${createdBy.lastName || ''}`.trim();
          } else if (typeof createdBy === 'string') {
            const userName = getUserName(createdBy);
            createdByName = userName || createdBy;
          }
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {colorByAttribute === 'createdBy' &&
              record.createdBy &&
              actionItemColors[createdByName] && (
                <span
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: actionItemColors[createdByName],
                    marginRight: '8px',
                    flexShrink: 0,
                  }}
                />
              )}
            {createdByName}
          </div>
        );
      },
    },
  ], [actionItem, colorByAttribute, actionItemColors, setFilterVisible, setActionItemCurrentFilter, setFilterHistory, actionItemCurrentFilter, columnWidths, handleResize, getUserName, formatChangeValue]);

  const [visibleColumns, setVisibleColumns] = useState(() => allColumns.map((col) => col.key));

  // Update visibleColumns when allColumns changes
  useEffect(() => {
    setVisibleColumns(allColumns.map((col) => col.key));
  }, [allColumns]);

  const filteredColumns = useMemo(() => allColumns.filter((col) => visibleColumns.includes(col.key)), [allColumns, visibleColumns]);
  const visibleFiltered = useMemo(() => allColumns.filter(
    (col) => visibleColumn?.includes(col.key) || col.key === colorByAttribute
  ), [allColumns, visibleColumn, colorByAttribute]);
  const displayedColumn = useMemo(() => visibleColumn ? visibleFiltered : filteredColumns, [visibleColumn, visibleFiltered, filteredColumns]);

  const menu = useMemo(() => (
    <Menu>
      {allColumns.map((col) => (
        <Menu.Item key={col.key}>
          <Checkbox
            checked={visibleColumns.includes(col.key)}
            onChange={(e) => {
              const { checked } = e.target;
              setVisibleColumns((prev) =>
                checked ? [...prev, col.key] : prev.filter((key) => key !== col.key)
              );
            }}
          >
            {col.title}
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  ), [allColumns, visibleColumns]);

  // Components for resize functionality
  const components = {
    header: {
      cell: ResizableTitle,
    },
  };

  // Memoize handlers
  const handleTableChange = useCallback((pagination, filters, sorter, extra) => {
    setFilteredData(extra.currentDataSource);
  }, []);

  const handleExport = useCallback((type) => {
    const dataToExport = type === 'filtered' ? filteredData : actionItem;
    const csvContent = [
      allColumns.map((col) => col.title),
      ...dataToExport.map((item) =>
        allColumns.map((col) => {
          const dataIndex = Array.isArray(col.dataIndex)
            ? col.dataIndex.reduce((acc, key) => acc?.[key], item)
            : item[col.dataIndex];
          return dataIndex || '';
        })
      ),
    ]
      .map((row) => row.join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, 'export.csv');
  }, [filteredData, actionItem, allColumns]);

  const formatCollaboratorNames = useCallback((collaboratorIds) => {
    if (!collaboratorIds || !Array.isArray(collaboratorIds) || !users) {
      return '';
    }
    return collaboratorIds
      .map((id) => {
        const user = users.find((u) => u._id === id);
        return user ? `${user.firstName} ${user.lastName}` : id;
      })
      .join(', ');
  }, [users]);

  const getChangeDetailsData = useCallback((changeDetails) => {
    if (!changeDetails) return [];
    if (changeDetails.collaborator) {
      return [
        {
          key: '1',
          old: formatCollaboratorNames(changeDetails.collaborator.old),
          new: formatCollaboratorNames(changeDetails.collaborator.new),
        },
      ];
    }
    return [
      {
        key: '1',
        old: formatChangeValue(changeDetails.old),
        new: formatChangeValue(changeDetails.new),
      },
    ];
  }, [formatCollaboratorNames, formatChangeValue]);

  // Calculate dynamic padding
  useEffect(() => {
    const calculatePadding = () => {
      if (otherElementRef && otherElementRef.current) {
        setDynamicPadding(otherElementRef.current.offsetWidth + 20);
      } else {
        setDynamicPadding(20);
      }
    };
    calculatePadding();
    window.addEventListener('resize', calculatePadding);
    return () => window.removeEventListener('resize', calculatePadding);
  }, [otherElementRef]);

  // Set form values when selectedRecord changes
  useEffect(() => {
    if (selectedRecord) {
      form.setFieldsValue({
        ...selectedRecord,
        owner: selectedRecord.owner,
        collaborator: selectedRecord.collaborator,
        dueDate: selectedRecord.dueDate ? moment(selectedRecord.dueDate) : null,
        source: selectedRecord.source || 'TO',
      });

      const combinedComments = [];

      // Add the initial comment from the comment field (not from comments array)
      if (selectedRecord.comment) {
        combinedComments.push({
          comment: selectedRecord.comment,
          commentedBy: selectedRecord.createdBy,
          commentedDate: selectedRecord.created,
        });
      }

      // Add additional comments from the comments array
      if (selectedRecord.comments && selectedRecord.comments.length > 0) {
        selectedRecord.comments.forEach((c) => {
          combinedComments.push({
            comment: c.comment,
            commentedBy: c.commentedBy,
            commentedDate: c.commentedDate,
          });
        });
      }

      // Sort comments in descending order (newest first)
      const sortedComments = combinedComments.sort((a, b) =>
        new Date(b.commentedDate) - new Date(a.commentedDate)
      );
      setComments(sortedComments);
    }
  }, [selectedRecord, form, users]);

  // Load attachments when selectedRecord changes
  useEffect(() => {
    if (selectedRecord) {
      setAttachments(selectedRecord.attachment || []);
      setFileList([]);
      attachmentForm.resetFields(['typeOfAttachment']);
    }
  }, [selectedRecord, attachmentForm]);

  const showModal = useCallback((record) => {
    setSelectedRecord(record);
    form.resetFields();
    setTimeout(() => {
      form.setFieldsValue({
        actionItemNumber: record.actionItemNumber,
        actionItem: record.actionItem,
        owner: record.owner?._id || record.owner,
        selectedForToMeeting: record?.selectedForToMeeting,
        collaborator: record.collaborator || [],
        initiative: record.initiative,
        workstream: record.workstream,
        status: record.status,
        dueDate: record.dueDate ? moment(record.dueDate) : null,
        source: record.source || 'TO',
      });
    }, 0);
    setIsModalVisible(true);
  }, [form]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
    setNewComment('');
    setChangeDetails(null);
    setModalVisible(false);
    setSelectedRecord(null);
    setFileList([]);
    attachmentForm.resetFields(['typeOfAttachment']);
    setDetailsModalVisible(false);
  }, [attachmentForm]);

  const showChangeDetails = useCallback((changes) => {
    setChangeDetails(changes);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setChangeDetails(null);
  }, []);

  const handleSaveField = useCallback(async (fieldName, fieldValue) => {
    if (!selectedRecord) return;
    setSaving(true);
    const requestData = {
      [fieldName]: fieldValue,
    };
    try {
      const res = await makeApiRequest(
        'patch',
        `/workstreamaction/update/${selectedRecord._id}`,
        requestData,
        'Field updated successfully',
        'Failed to update field'
      );
      if (res && res.success) {
        setActionItem((prevItems) =>
          prevItems.map((item) =>
            item._id === selectedRecord._id ? { ...item, [fieldName]: fieldValue } : item
          )
        );
        setSelectedRecord((prevRecord) => ({ ...prevRecord, [fieldName]: fieldValue }));
        message.success('Field updated successfully');
      }
    } catch (error) {
      message.error('Failed to update field');
    } finally {
      setSaving(false);
    }
  }, [selectedRecord]);

  const handleStatusChange = useCallback((value) => {
    handleSaveField('status', value);
  }, [handleSaveField]);

  const handleDueDateChange = useCallback((date) => {
    handleSaveField('dueDate', date);
  }, [handleSaveField]);

  const handleSourceChange = useCallback((value) => {
    handleSaveField('source', value);
  }, [handleSaveField]);

  const handleBlur = useCallback(async (fieldName, fieldValue) => {
    await handleSaveField(fieldName, fieldValue);
  }, [handleSaveField]);

  const handleAddComment = useCallback(async () => {
    if (newComment.trim() && selectedRecord) {
      const newCommentObj = {
        comment: newComment,
        commentedBy: {
          _id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
        },
        commentedDate: new Date(),
      };

      // Get existing comments or initialize empty array
      const existingComments = selectedRecord.comments || [];
      const updatedComments = [newCommentObj, ...existingComments];

      try {
        // Only update the comments array, NOT the comment field
        const requestData = {
          comments: updatedComments.map((c) => ({
            comment: c.comment,
            commentedBy: c.commentedBy?._id || c.commentedBy || currentUser.id,
            commentedDate: c.commentedDate,
          })),
        };

        const res = await makeApiRequest(
          'put',
          `/workstreamaction/update/${selectedRecord._id}`,
          requestData,
          'Comment added successfully',
          'Failed to add comment'
        );

        if (res && res.success) {
          // Update the selected record with the new comments array
          const updatedRecord = {
            ...selectedRecord,
            comments: updatedComments
          };

          // Update local state
          const allComments = [];
          if (updatedRecord.comment) {
            allComments.push({
              comment: updatedRecord.comment,
              commentedBy: updatedRecord.createdBy,
              commentedDate: updatedRecord.created,
            });
          }
          updatedComments.forEach((c) => {
            allComments.push({
              comment: c.comment,
              commentedBy: c.commentedBy,
              commentedDate: c.commentedDate,
            });
          });
          const sortedComments = allComments.sort((a, b) =>
            new Date(b.commentedDate) - new Date(a.commentedDate)
          );

          setComments(sortedComments);
          setNewComment('');
          setSelectedRecord(updatedRecord);

          // Update actionItem list
          setActionItem((prevItems) =>
            prevItems.map((item) =>
              item._id === selectedRecord._id ? updatedRecord : item
            )
          );

          // Update filteredData
          setFilteredData((prevItems) =>
            prevItems.map((item) =>
              item._id === selectedRecord._id ? updatedRecord : item
            )
          );

          message.success('Comment added successfully');
        }
      } catch (error) {
        console.error('Error updating comments:', error);
        message.error('Failed to add comment');
      }
    }
  }, [newComment, selectedRecord, currentUser]);

  const getIcon = useCallback((event) => {
    if (event.toLowerCase().includes('created')) {
      return <PlusCircleOutlined style={{ color: '#52c41a' }} />;
    } else if (event.toLowerCase().includes('updated')) {
      return <SyncOutlined style={{ color: '#1890ff' }} />;
    } else if (event.toLowerCase().includes('deleted')) {
      return <DeleteOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <PaperClipOutlined style={{ color: '#faad14' }} />;
  }, []);

  const handleAddNewInitiative = useCallback(() => {
    history.push('/initiative');
  }, [history]);

  const handleAddNewWorkstreamAction = useCallback(() => {
    history.push('/workstreamaction');
  }, [history]);

  const addMenu = useMemo(() => (
    <Menu style={{ background: '#253271', color: 'white' }}>
      <Menu.Item key="add-initiative" onClick={handleAddNewInitiative} style={{ color: 'white' }}>
        Add New Initiative
      </Menu.Item>
      <Menu.Item
        key="add-workstream-action"
        onClick={handleAddNewWorkstreamAction}
        style={{ color: 'white' }}
      >
        Add New Workstream Action
      </Menu.Item>
    </Menu>
  ), [handleAddNewInitiative, handleAddNewWorkstreamAction]);

  const handleCopyLinkClick = useCallback(() => {
    const currentUrl = window.location.href;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          alert('Link copied to clipboard!');
        })
        .catch((err) => {
          console.error('Failed to copy link: ', err);
          fallbackCopyToClipboard(currentUrl);
        });
    } else {
      fallbackCopyToClipboard(currentUrl);
    }
  }, []);

  const fallbackCopyToClipboard = useCallback((text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      const msg = successful ? 'Link copied to clipboard!' : 'Failed to copy link';
      alert(msg);
    } catch (err) {
      console.error('Fallback: Failed to copy link: ', err);
      alert('Failed to copy link. Please try manually.');
    }
    document.body.removeChild(textArea);
  }, []);

  const changesColumns = useMemo(() => [
    {
      title: 'Old Value',
      dataIndex: 'old',
      key: 'old',
    },
    {
      title: 'New Value',
      dataIndex: 'new',
      key: 'new',
    },
  ], []);

  // Sort activities in descending order (newest first)
  const sortedActivities = useMemo(() => {
    if (!selectedRecord?.activity) return [];
    return [...selectedRecord.activity].sort((a, b) =>
      new Date(b.eventDate) - new Date(a.eventDate)
    );
  }, [selectedRecord?.activity]);

  // ----- Attachment Functions -----
  const handleFileChange = (info) => {
    setFileList(info.fileList);
  };

  const handleDownloadAttachment = async (filePath) => {
    try {
      const fileName = filePath.split('/').pop();
      const encodedName = encodeURIComponent(fileName);
      const downloadUrl = `/download/${encodedName}`;

      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
        withCredentials: true,
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);

        notification.success({
          message: 'Download successful',
          description: 'File downloaded successfully',
          duration: 3,
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      notification.error({
        message: 'Download failed',
        description: error?.response?.data?.message || error?.message || 'Failed to download file',
        duration: 10,
      });
    }
  };

  const handleUploadAttachments = async () => {
    if (fileList.length === 0) {
      message.error('No files selected for upload');
      return;
    }

    if (!selectedRecord?._id) {
      message.error('No action item selected');
      return;
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      formData.append('attachment[]', file.originFileObj);
    });

    const attachmentType = attachmentForm.getFieldValue('typeOfAttachment');
    formData.append('typeOfAttachment', attachmentType || 'General');

    setUploading(true);

    try {
      const response = await axios.post(
        `/workstreamaction/${selectedRecord._id}/attachment`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      const data = response.data;

      if (data && data.attachment && Array.isArray(data.attachment)) {
        const newAttachments = data.attachment;

        const existingAttachments = selectedRecord?.attachment || [];
        const existingPaths = existingAttachments.map(a =>
          typeof a === 'string' ? a : a.attachment
        );

        const newlyAdded = newAttachments.filter(a => {
          const path = typeof a === 'string' ? a : a.attachment;
          return !existingPaths.includes(path);
        });

        setAttachments(newAttachments);

        setSelectedRecord((prev) => ({
          ...prev,
          attachment: newAttachments
        }));

        setActionItem((prevItems) =>
          prevItems.map((item) =>
            item._id === selectedRecord._id
              ? { ...item, attachment: newAttachments }
              : item
          )
        );

        notification.success({
          message: 'Upload successful',
          description: `Files uploaded successfully (${newlyAdded.length} file(s))`,
          duration: 5,
        });

        setFileList([]);
        attachmentForm.resetFields(['typeOfAttachment']);
      } else {
        notification.error({
          message: 'Upload completed but response format unexpected',
          description: 'Files were uploaded but the response format was unexpected. Refreshing data...',
          duration: 10,
        });

        try {
          const refreshResponse = await axios.get(
            `/workstreamaction/read/${selectedRecord._id}`,
            { withCredentials: true }
          );

          if (refreshResponse.data && refreshResponse.data.attachment) {
            const updatedAttachments = refreshResponse.data.attachment;
            setAttachments(updatedAttachments);
            setSelectedRecord((prev) => ({
              ...prev,
              attachment: updatedAttachments
            }));
            setActionItem((prevItems) =>
              prevItems.map((item) =>
                item._id === selectedRecord._id
                  ? { ...item, attachment: updatedAttachments }
                  : item
              )
            );
            notification.success({
              message: 'Data refreshed',
              description: 'Attachment list has been updated',
              duration: 3,
            });
          }
        } catch (refreshError) {
          console.error('Error refreshing data:', refreshError);
          notification.error({
            message: 'Failed to refresh data',
            description: 'Please refresh the page manually to see the uploaded files',
            duration: 10,
          });
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      notification.error({
        message: 'Upload failed',
        description: error?.response?.data?.message || error?.message || 'Failed to upload files',
        duration: 10,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (filePath) => {
    if (!selectedRecord?._id) {
      message.error('No action item selected');
      return;
    }

    try {
      const response = await axios.delete(
        `/workstreamaction/${selectedRecord._id}/attachment?filePath=${encodeURIComponent(filePath)}`,
        {
          withCredentials: true,
        }
      );

      const data = response.data;

      if (data && data.success) {
        setAttachments((prev) =>
          prev.filter((file) => {
            const filePathToCheck = typeof file === 'string'
              ? file.replace(/\\/g, '/')
              : file.attachment?.replace(/\\/g, '/') || '';
            return filePathToCheck !== filePath;
          })
        );

        setSelectedRecord((prev) => ({
          ...prev,
          attachment: (prev.attachment || []).filter((file) => {
            const filePathToCheck = typeof file === 'string'
              ? file.replace(/\\/g, '/')
              : file.attachment?.replace(/\\/g, '/') || '';
            return filePathToCheck !== filePath;
          })
        }));

        setActionItem((prevItems) =>
          prevItems.map((item) =>
            item._id === selectedRecord._id
              ? {
                ...item,
                attachment: (item.attachment || []).filter((file) => {
                  const filePathToCheck = typeof file === 'string'
                    ? file.replace(/\\/g, '/')
                    : file.attachment?.replace(/\\/g, '/') || '';
                  return filePathToCheck !== filePath;
                })
              }
              : item
          )
        );

        notification.success({
          message: 'Delete successful',
          description: 'Attachment deleted successfully',
          duration: 5,
        });
      } else {
        notification.error({
          message: 'Delete failed',
          description: data.message || 'Failed to delete attachment',
          duration: 10,
        });
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
      notification.error({
        message: 'Delete failed',
        description: error?.response?.data?.message || error?.message || 'Failed to delete attachment',
        duration: 10,
      });
    }
  };

  // Attachment Table Columns for dedicated tab
  const attachmentColumns = useMemo(() => [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => <span>{text || ''}</span>,
    },
    {
      title: 'Uploaded Date',
      dataIndex: 'uploadedDate',
      key: 'uploadedDate',
      render: (date) => date ? moment(date).format('YYYY-MM-DD HH:mm') : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Download">
            <DownloadOutlined
              onClick={() => handleDownloadAttachment(record.path)}
              style={{ cursor: 'pointer', color: '#1890ff', fontSize: '16px' }}
            />
          </Tooltip>
          {allowToEdit && (
            <Popconfirm
              title="Are you sure you want to delete this attachment?"
              onConfirm={() => handleDeleteAttachment(record.path)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <DeleteOutlined style={{ cursor: 'pointer', color: '#ff4d4f', fontSize: '16px' }} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ], [allowToEdit, handleDeleteAttachment]);

  const attachmentDataSource = useMemo(() => {
    return (selectedRecord?.attachment || [])
      .map((file, index) => {
        let filePath = '';
        let attachmentType = 'Unknown';
        let uploadedDate = null;

        if (typeof file === 'string') {
          filePath = file;
        } else if (typeof file === 'object' && file.attachment) {
          filePath = file.attachment;
          attachmentType = file.typeOfAttachment || 'Unknown';
          uploadedDate = file.created || file.uploadedDate || null;
        }

        if (!filePath) return null;

        const normalizedPath = filePath.replace(/\\/g, '/');
        const fileName = normalizedPath.split('/').pop();

        return {
          key: index.toString(),
          name: fileName,
          path: normalizedPath,
          type: attachmentType,
          uploadedDate: uploadedDate,
        };
      })
      .filter(Boolean);
  }, [selectedRecord?.attachment]);

  const attachmentSelectOptions = useMemo(() => {
    return [
      { label: 'TO', value: 'TO' },
      { label: 'SteerCo', value: 'SteerCo' },
      { label: 'CMC', value: 'CMC' },
      { label: 'AMC', value: 'AMC' },
      { label: 'EMC', value: 'EMC' },
      { label: 'Office of CEO', value: 'Office of CEO' },
      { label: 'Other', value: 'Other' },
    ];
  }, []);

  // Open details modal
  const openDetailsModal = useCallback(() => {
    setDetailsModalVisible(true);
  }, []);

  // Close details modal
  const closeDetailsModal = useCallback(() => {
    setDetailsModalVisible(false);
  }, []);

  // Combined all items for the "All" tab with clear separation
  const allItemsWithSections = useMemo(() => {
    const sections = [];

    // 1. Attachments Section (First)
    if (attachmentDataSource.length > 0) {
      sections.push({
        type: 'section',
        title: 'Attachments',
        icon: <PaperClipOutlined style={{ color: '#1890ff', marginRight: '8px' }} />,
        count: attachmentDataSource.length,
        items: attachmentDataSource.map(att => ({
          type: 'attachment',
          data: att,
          timestamp: att.uploadedDate,
          render: () => (
            <MemoizedAttachmentItem
              attachment={att}
              handleDownloadAttachment={handleDownloadAttachment}
              allowToEdit={allowToEdit}
              handleDeleteAttachment={handleDeleteAttachment}
            />
          )
        }))
      });
    }

    // 2. Activities Section (Second)
    if (sortedActivities.length > 0) {
      sections.push({
        type: 'section',
        title: 'Activities',
        icon: <ClockCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />,
        count: sortedActivities.length,
        items: sortedActivities.map(activity => ({
          type: 'activity',
          data: activity,
          timestamp: activity.eventDate,
          render: () => (
            <MemoizedActivityItem
              activity={activity}
              getIcon={getIcon}
              showChangeDetails={showChangeDetails}
            />
          )
        }))
      });
    }

    // 3. Comments Section (Third)
    if (comments.length > 0) {
      sections.push({
        type: 'section',
        title: 'Comments',
        icon: <CommentOutlined style={{ color: '#faad14', marginRight: '8px' }} />,
        count: comments.length,
        items: comments.map(comment => ({
          type: 'comment',
          data: comment,
          timestamp: comment.commentedDate,
          render: () => <MemoizedCommentItem commentObj={comment} />
        }))
      });
    }

    return sections;
  }, [attachmentDataSource, sortedActivities, comments, allowToEdit]);

  // Flatten all items for the "All" tab with section headers
  const allItems = useMemo(() => {
    const items = [];

    allItemsWithSections.forEach(section => {
      // Sort items within each section by timestamp (newest first)
      const sortedItems = [...section.items].sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateB - dateA;
      });

      items.push({
        isSection: true,
        title: section.title,
        icon: section.icon,
        count: section.count,
        items: sortedItems
      });
    });

    return items;
  }, [allItemsWithSections]);

  return (
    <div style={{ overflowX: 'auto', width: '100%', marginLeft: '-150px', marginTop: '50px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Dropdown overlay={menu} trigger={['click']}>
          <Button>Show/Hide Columns</Button>
        </Dropdown>
      </div>
      <Table
        components={components}
        columns={displayedColumn}
        dataSource={filteredData}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
        style={{ width: '100%' }}
        onRow={(record) => ({
          onClick: () => showModal(record),
        })}
        rowKey="_id"
      />
      <Modal
        title={
          <div
            style={{
              backgroundColor: '#253271',
              color: 'white',
              height: '40px',
              marginTop: '-18px',
              width: '100%',
              marginLeft: '-25px',
              marginBottom: '-100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
              <FolderOutlined />
              <span style={{ marginLeft: '8px' }}>
                Workstream Action #{selectedRecord && selectedRecord?.actionItemNumber}
              </span>
            </span>
            <span
              style={{
                display: 'flex',
                gap: '10px',
                marginRight: '10px',
              }}
            >
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={openDetailsModal}
                style={{ color: 'white' }}
              >
                Details
              </Button>
              <Dropdown overlay={addMenu} trigger={['click']}>
                <PlusOutlined style={{ color: 'white', cursor: 'pointer' }} />
              </Dropdown>
              <SendOutlined onClick={handleCopyLinkClick} style={{ color: 'white', cursor: 'pointer' }} />
            </span>
          </div>
        }
        bodyStyle={{
          overflowY: 'auto',
          height: '670px',
          padding: '24px',
        }}
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width="100%"
        style={{
          top: 20,
          maxHeight: '600px'
        }}
      >
        <div style={{ display: 'flex', width: '100%', height: '100%' }}>
          {/* Left Column - Form Fields - Takes 100% width */}
          <div style={{ width: '100%', paddingRight: '0px' }}>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Workstream Action Description" name="workstreamActionName">
                    <TextArea
                      onBlur={(e) => handleBlur('workstreamActionName', e.target.value)}
                      disabled={!allowToEdit}
                      style={{ fontSize: '18px' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Owner"
                    name="owner"
                    rules={[{ required: true, message: 'Please select User !' }]}
                  >
                    <AutoCompleteAsync
                      entity={obfucatePath}
                      displayLabels={['firstName', 'lastName']}
                      searchFields={['firstName', 'lastName']}
                      handleSave={handleSaveField}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Selected for TO Meeting Discussion"
                    name="selectedForToMeeting"
                    valuePropName="checked"
                    initialValue={false}
                  >
                    <Switch checkedChildren="Yes" unCheckedChildren="No" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Collaborator"
                    name="collaborator"
                  >
                    <ActionMultipleAutoCompleteAsync
                      entity={obfucatePath}
                      displayLabels={['firstName', 'lastName']}
                      searchFields={['firstName', 'lastName']}
                      handleSave={handleSaveField}
                      disabled={!allowToEdit}
                      value={selectedRecord?.collaborator}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dueDate" label="Due Date">
                    <DatePicker
                      style={{ width: '100%' }}
                      showTime
                      onChange={handleDueDateChange}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="status"
                    label="Status"
                    rules={[{ required: true, message: 'Please select Status!' }]}
                    initialValue="Not Completed"
                  >
                    <Select
                      placeholder="Select a Status"
                      onChange={handleStatusChange}
                      disabled={!allowToEdit}
                    >
                      <Option value="Not Completed">Not Completed</Option>
                      <Option value="Completed">Completed</Option>
                      <Option value="Cancelled">Cancelled</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="source"
                    label="Source"
                    rules={[
                      {
                        required: true,
                        message: 'Please select Source!',
                      },
                    ]}
                    initialValue="TO"
                  >
                    <Select
                      placeholder="Select a Source"
                      onChange={handleSourceChange}
                      disabled={!allowToEdit}
                    >
                      <Option value="TO">TO</Option>
                      <Option value="SteerCo">SteerCo</Option>
                      <Option value="CMC">CMC</Option>
                      <Option value="AMC">AMC</Option>
                      <Option value="EMC">EMC</Option>
                      <Option value="Office of CEO">Office of CEO</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Comment" name="comment">
                    <TextArea
                      rows={10}
                      onBlur={(e) => handleBlur('comment', e.target.value)}
                      disabled={!allowToEdit}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </Modal>

      {/* Details Modal - Overlay for Attachments, Comments, Activities */}
      <Modal
        title={
          <div
            style={{
              backgroundColor: '#253271',
              color: 'white',
              height: '40px',
              marginTop: '-18px',
              width: '100%',
              marginLeft: '-25px',
              marginBottom: '-100px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
              <FolderOutlined />
              <span style={{ marginLeft: '8px' }}>
                Details - Workstream Action #{selectedRecord && selectedRecord?.actionItemNumber}
              </span>
            </span>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={closeDetailsModal}
              style={{ color: 'white', marginRight: '10px' }}
            />
          </div>
        }
        bodyStyle={{
          padding: '0',
          height: '550px',
        }}
        visible={detailsModalVisible}
        onCancel={closeDetailsModal}
        footer={null}
        width="80%"
        style={{
          top: 20,
          maxHeight: '600px',
        }}
      >
        <Tabs
          defaultActiveKey="attachments"
          activeKey={selectedTab}
          onChange={setSelectedTab}
          style={{ height: '100%' }}
          tabBarStyle={{ padding: '0 24px', marginBottom: '0' }}
        >
          {/* Attachments Tab */}
          <TabPane
            tab={
              <span>
                <PaperClipOutlined /> Attachments
              </span>
            }
            key="attachments"
          >
            <div style={{ padding: '16px 24px', height: '460px', overflowY: 'auto' }}>
              {/* Upload Section */}
              <div style={{
                marginBottom: 16,
                padding: '16px',
                background: '#f5f5f5',
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
              }}>
                <Form form={attachmentForm} layout="vertical">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Select Files" style={{ marginBottom: 8 }}>
                        <Upload
                          fileList={fileList}
                          onChange={handleFileChange}
                          beforeUpload={() => false}
                          disabled={!allowToEdit}
                        >
                          <Button icon={<PlusOutlined />} disabled={!allowToEdit}>
                            Choose Files
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="Type"
                        name="typeOfAttachment"
                        initialValue="General"
                        style={{ marginBottom: 8 }}
                      >
                        <Select options={attachmentSelectOptions} />
                      </Form.Item>
                    </Col>
                    <Col span={4} style={{ display: 'flex', alignItems: 'flex-end', marginBottom: '8px' }}>
                      <Button
                        type="primary"
                        icon={<UploadOutlined />}
                        onClick={handleUploadAttachments}
                        loading={uploading}
                        disabled={fileList.length === 0 || !allowToEdit}
                        block
                      >
                        Upload
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>

              {/* Attachment List */}
              {attachmentDataSource.length > 0 ? (
                <Table
                  dataSource={attachmentDataSource}
                  columns={attachmentColumns}
                  pagination={false}
                  scroll={{ y: 280 }}
                  rowKey="key"
                />
              ) : (
                <p style={{ color: '#999', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  No attachments found
                </p>
              )}
            </div>
          </TabPane>

          {/* Comments Tab */}
          <TabPane
            tab={
              <span>
                <CommentOutlined /> Comments
              </span>
            }
            key="comments"
          >
            <div style={{ padding: '16px 24px', height: '460px', overflowY: 'auto' }}>
              {comments.length > 0 ? (
                comments.map((commentObj, index) => (
                  <MemoizedCommentItem key={commentObj.id || index} commentObj={commentObj} />
                ))
              ) : (
                <p style={{ color: '#999', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  No comments yet
                </p>
              )}

              {/* Add Comment */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid #e8e8e8',
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'white',
                paddingBottom: '8px',
              }}>
                <Input.TextArea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  style={{ flex: 1, marginRight: '8px' }}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                  placeholder="Write a comment..."
                  onPressEnter={(e) => {
                    if (e.shiftKey) {
                      return;
                    }
                    e.preventDefault();
                    handleAddComment();
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                >
                  Send
                </Button>
              </div>
            </div>
          </TabPane>

          {/* Activities Tab */}
          <TabPane
            tab={
              <span>
                <ClockCircleOutlined /> Activities
              </span>
            }
            key="activities"
          >
            <div style={{ padding: '16px 24px', height: '460px', overflowY: 'auto' }}>
              {sortedActivities.length > 0 ? (
                sortedActivities.map((activity, index) => (
                  <MemoizedActivityItem
                    key={activity.id || index}
                    activity={activity}
                    getIcon={getIcon}
                    showChangeDetails={showChangeDetails}
                  />
                ))
              ) : (
                <p style={{ color: '#999', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  No activities yet
                </p>
              )}
            </div>
          </TabPane>
          {/* All Tab - First (Left) */}
          <TabPane
            tab={
              <span>
                <FileOutlined /> All (Attachments, Activities, Comments)
              </span>
            }
            key="all"
          >
            <div style={{ padding: '16px 24px', height: '460px', overflowY: 'auto' }}>
              {allItems.length > 0 ? (
                allItems.map((section, sectionIndex) => (
                  <div key={sectionIndex}>
                    {/* Section Title */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '10px 16px',
                      backgroundColor: section.title === 'Attachments' ? '#e6f7ff' :
                        section.title === 'Activities' ? '#f6ffed' :
                          '#fff7e6',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      marginTop: sectionIndex > 0 ? '24px' : '0',
                      borderLeft: section.title === 'Attachments' ? '4px solid #1890ff' :
                        section.title === 'Activities' ? '4px solid #52c41a' :
                          '4px solid #faad14',
                    }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }}>{section.icon}</span>
                      <strong style={{ fontSize: '15px', color: '#333' }}>
                        {section.title} ({section.count})
                      </strong>
                    </div>

                    {/* Section Items */}
                    {section.items.length > 0 ? (
                      section.items.map((item, itemIndex) => (
                        <div key={itemIndex}>
                          {item.render()}
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#999', fontStyle: 'italic', padding: '8px 16px' }}>
                        No {section.title.toLowerCase()} available
                      </p>
                    )}
                  </div>
                ))
              ) : (
                <p style={{ color: '#999', fontStyle: 'italic', padding: '16px', textAlign: 'center' }}>
                  No items available
                </p>
              )}
            </div>
          </TabPane>
        </Tabs>

        {/* Change Details Modal */}
        <Modal
          title="Change Details"
          visible={changeDetails !== null}
          onCancel={handleCloseModal}
          footer={null}
          width={800}
        >
          {changeDetails && (
            <Table
              dataSource={getChangeDetailsData(changeDetails)}
              columns={changesColumns}
              pagination={false}
            />
          )}
        </Modal>
      </Modal>
    </div>
  );
};
export default WorkstreamactionTable;